import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { ChronoUnit, Duration, LocalDate } from "@js-joda/core";
import { motion } from "framer-motion";
import React from "react";
import { AddressComponents, PatientTaskInstanceId } from "../../../messages";
import { STRINGS } from "../../../strings";
import { AppCaregiver } from "../../../system/AppState/AppCaregiverState";
import { globalize, translate } from "../../../translate";
import { formatDuration } from "../../../utils/dateUtils";
import { getColor } from "../../../utils/generateColor";
import { ArrowCircleRightOutlineIcon } from "../../../utils/icones";
import { formatTaskAddress } from "../../../utils/taskUtils";
import { AppButton } from "../../Generic/AppButton";
import TravelTimeEstimator from "../../TravelTimesEstimator";

type Props = {
  caregiver: Pick<AppCaregiver, "addressGeoLocation">;
  id: PatientTaskInstanceId;
  status: "NEW" | "VIEWED" | "ASSIGNED" | "SCHEDULED";
  title: string;
  startDate: LocalDate;
  dueDate: LocalDate;
  durationMinutes: number | null;
  patientInitials: string;
  patientAddressComponents: AddressComponents | null;
  patientAddress: string | null;
  onClick: (taskInstanceId: PatientTaskInstanceId) => void;
};

function TaskMapCard(props: Props, ref: React.Ref<HTMLDivElement>) {
  const {
    title,
    startDate,
    dueDate,
    durationMinutes,
    patientInitials,
    patientAddressComponents,
    patientAddress,
  } = props;

  const address = formatTaskAddress({
    addressString: patientAddress,
    addressComponents: patientAddressComponents,
  });

  return (
    <Root ref={ref}>
      <Header>
        <Avatar>{patientInitials}</Avatar>
        <HeaderContent>
          <ScheduleDate>{formatDate(startDate, dueDate)}</ScheduleDate>
          {address !== null && <Address>{address}</Address>}
        </HeaderContent>
      </Header>
      <TaskSecondHeader>
        <TaskCertificationsAndDuration>
          <TaskCertifications>{title}</TaskCertifications>
          {durationMinutes !== null && (
            <>
              {" / "}
              <TaskDuration>
                {formatDuration(Duration.of(durationMinutes, ChronoUnit.MINUTES))}
              </TaskDuration>
            </>
          )}
        </TaskCertificationsAndDuration>
        <TaskMapCardStatus status={props.status} />
      </TaskSecondHeader>

      {patientAddressComponents !== null && (
        <>
          <Hr />
          <TravelTimeEstimator
            source={props.caregiver.addressGeoLocation}
            destination={patientAddressComponents.location}
          />
          <Hr />
        </>
      )}

      <ButtonWrapper>
        <TaskInformationButton size="large" onClick={() => props.onClick(props.id)}>
          <span>{translate(STRINGS.TASK_INFORMATION)}</span>
          <ArrowCircleRightOutlineIcon fontSize={24} />
        </TaskInformationButton>
      </ButtonWrapper>
    </Root>
  );
}

export default React.forwardRef(TaskMapCard);

function formatDate(startDateLocal: LocalDate, endDateLocal: LocalDate) {
  const format = (date: LocalDate) =>
    globalize().formatLocalDate(
      date,
      startDateLocal.year() === endDateLocal.year() ? { skeleton: "MMMd" } : { date: "short" }
    );

  const startDate = format(startDateLocal);
  const endDate = format(endDateLocal);

  return startDate === endDate ? startDate : `${startDate} - ${endDate}`;
}

const Root = styled(motion.div)`
  background: white;
  box-shadow: 0 0 8px rgba(0, 0, 0, 0.08);
  border-radius: 16px;
`;

const Header = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 12px;
  padding: 12px 12px 0;
  margin-bottom: 12px;
`;

const Avatar = styled.div<{ children: string }>`
  --size: 56px;
  background-color: ${({ children }) => getColor(children)};
  display: flex;
  justify-content: center;
  align-items: center;
  color: white;
  width: var(--size);
  height: var(--size);
  border-radius: 20px;
  font-size: 16px;
  font-weight: 700;
  border: 4px solid white;
  margin-top: -24px;
`;

const HeaderContent = styled.div`
  display: flex;
  flex-direction: column;
`;

const ScheduleDate = styled.span`
  font-weight: 700;
  font-size: 16px;
  line-height: 20px;
`;

const Address = styled.span`
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: var(--tw-slate-500);
`;

const TaskSecondHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 12px 16px;
`;

const TaskCertificationsAndDuration = styled.div`
  font-size: 14px;
  line-height: 20px;
`;

const TaskCertifications = styled.span`
  color: var(--tw-slate-500);
  font-weight: 600;
`;

const TaskDuration = styled.strong`
  color: var(--tw-slate-900);
  font-weight: 700;
  text-transform: lowercase;
`;

const Hr = styled.div`
  height: 1px;
  margin: 8px 0;
  background-color: var(--tw-slate-100);
`;

function TaskMapCardStatus(props: { status: "NEW" | "VIEWED" | "ASSIGNED" | "SCHEDULED" }) {
  const statusAsText = (() => {
    switch (props.status) {
      case "NEW":
        return translate(STRINGS.NEW);
      case "VIEWED":
        return translate(STRINGS.VIEWED);
      case "ASSIGNED":
        return translate(STRINGS.ASSIGNED);
      case "SCHEDULED":
        return translate(STRINGS.SCHEDULED);
    }
  })();

  return <Root2 status={props.status}>{statusAsText}</Root2>;
}

const Root2 = styled.div<{ status: "NEW" | "VIEWED" | "ASSIGNED" | "SCHEDULED" }>`
  --bg-color: black;
  --text-color: white;
  background-color: var(--bg-color);
  color: var(--text-color);
  border-radius: 8px;
  font-size: 14px;
  font-weight: 700;
  padding: 4px 8px;
  text-transform: capitalize;

  display: flex;

  ${(props) => {
    switch (getStatusColor(props.status)) {
      case "blue":
        return css`
          --bg-color: var(--tw-blue-50);
          --text-color: var(--tw-blue-600);
        `;
      case "yellow":
        return css`
          --bg-color: var(--tw-amber-50);
          --text-color: var(--tw-amber-600);
        `;
      case "slate":
        return css`
          --bg-color: var(--tw-slate-100);
          --text-color: var(--tw-slate-600);
        `;
    }
  }}
`;

const ButtonWrapper = styled.div`
  margin-top: 12px;
  padding: 0 12px 12px 12px;
`;

const TaskInformationButton = styled(AppButton)`
  font-size: 16px;
  font-weight: 600;
  width: 100%;
`;

function getStatusColor(status: "NEW" | "VIEWED" | "ASSIGNED" | "SCHEDULED") {
  switch (status) {
    case "NEW":
      return "blue";
    case "VIEWED":
      return "slate";
    case "ASSIGNED":
      return "yellow";
    case "SCHEDULED":
      return "blue";
  }
}
